import { type ClientError, clientErrorSchema } from "@app/protocol";
import { API_BASE } from "../config";
import { useStore } from "../store";
import { clip, nextReport, type ReportLedger } from "./throttle";

let ledger: ReportLedger = { sent: 0, seen: new Set() };

/**
 * Reports an error to the server and returns the event id to show the player (review 0019 S8).
 * Best effort: a report that cannot be sent is dropped; nothing here may throw.
 */
export function reportError(
  kind: ClientError["kind"],
  error: unknown,
  extra: { stage?: string | undefined; platform?: string | undefined } = {},
): string {
  const eventId = crypto.randomUUID();
  const message = clip(error instanceof Error ? error.message : String(error), 500) ?? "";
  const step = nextReport(ledger, `${kind}:${message}`);
  ledger = step.ledger;
  if (!step.send) return eventId;
  const stack = error instanceof Error ? clip(error.stack, 2000) : undefined;
  const report = clientErrorSchema.safeParse({ kind, eventId, message, stack, ...extra });
  if (!report.success) return eventId;
  const session = useStore.getState().session;
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (session !== null) headers.Authorization = `Bearer ${session}`;
  void fetch(`${API_BASE}/client-errors`, {
    method: "POST",
    headers,
    body: JSON.stringify(report.data),
    keepalive: true,
  }).catch(() => undefined);
  return eventId;
}

/** Errors nothing else caught: uncaught exceptions and rejected promises. */
export function installErrorReports(): void {
  window.addEventListener("error", (event) => {
    reportError("error", event.error ?? event.message);
  });
  window.addEventListener("unhandledrejection", (event) => {
    reportError("rejection", event.reason);
  });
}

/** The short form players read out to support. */
export const shortId = (eventId: string) => eventId.slice(0, 8);
